// packages/web/src/lib/url-utils.ts
// URL utility functions for cross-domain embedding support

/**
 * Get the platform domain from environment variable or detect from context
 * Supports both dev (visionarynow.com) and prod (aithon.tech)
 */
export function getPlatformDomain(): string {
  // Check environment variable first
  if (process.env.NEXT_PUBLIC_PLATFORM_DOMAIN) {
    return process.env.NEXT_PUBLIC_PLATFORM_DOMAIN;
  }

  if (typeof window !== 'undefined') {
    // Client-side: use current hostname for automatic detection
    return window.location.hostname;
  }

  // Server-side: use NODE_ENV detection
  return process.env.NODE_ENV === 'production' ? 'aithon.tech' : 'visionarynow.com';
}

/**
 * Get the full platform URL for a given path
 * @param relativeUrl - The relative URL (e.g., '/assets/brands/default/logo.png')
 * @returns Absolute URL (e.g., 'https://aithon.tech/assets/brands/default/logo.png')
 */
export function getPlatformUrl(relativeUrl: string = '/'): string {
  const domain = getPlatformDomain();
  const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  return `https://${domain}${path}`;
}

/**
 * Convert a relative URL to an absolute URL using the platform domain
 * This fixes the issue where relative URLs break when embedded in iframes on different domains
 *
 * @param relativeUrl - The relative URL (e.g., '/assets/brands/default/logo.png')
 * @returns Absolute URL
 */
export function toAbsoluteUrl(relativeUrl: string | null | undefined): string {
  if (!relativeUrl) return '';

  // Already absolute URL
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  // Data URLs should be returned as-is
  if (relativeUrl.startsWith('data:')) {
    return relativeUrl;
  }

  // Protocol-relative URL
  if (relativeUrl.startsWith('//')) {
    return `https:${relativeUrl}`;
  }

  // Ensure leading slash for relative paths
  const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;

  // Build absolute URL using platform domain
  const domain = getPlatformDomain();
  return `https://${domain}${path}`;
}

/**
 * Generate a complete base tag HTML for embedding
 *
 * @returns HTML string for the base tag
 */
export function getBaseTagHtml(): string {
  const domain = getPlatformDomain();
  return `<base href="https://${domain}/">`;
}

// Export for convenience
export const PLATFORM_DOMAIN = getPlatformDomain();
